/**
 * @fileoverview Zod schemas and types for Sales feature.
 * @module features/sales/schemas/salesSchema
 */

import { z } from 'zod'

/**
 * Currency schema aligned with API docs.
 */
/**
 * Backend returns currency as an ISO currency code string (e.g., "PLN"),
 * not the full currency object. Accept both forms and normalize to code strings.
 */
export const currencySchema = z.union([
  z.string(), // "PLN"
  z.object({
    currencyCode: z.string(),
    numericCode: z.number(),
    numericCodeAsString: z.string(),
    displayName: z.string(),
    symbol: z.string(),
    defaultFractionDigits: z.number(),
  }),
])

const currencyCodeSchema = currencySchema.transform(val =>
  typeof val === 'string' ? val : val.currencyCode
)

/**
 * SKU schema embedded in SaleDTO.
 */
/**
 * SKU fields are often nullable/optional in API payloads.
 * Loosen the schema to accept null/undefined and empty strings.
 */
export const saleSkuSchema = z.object({
  id: z.number(),
  productExternalId: z.string().nullable().optional(),
  name: z.string(),
  productName: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  description: z.string().nullable().optional().catch(null),
  category: z.string().nullable().optional().catch(null),
  brand: z.string().nullable().optional().catch(null),
  model: z.string().nullable().optional().catch(null),
  originalCost: z.number().nullable().optional(),
  originalCurrency: currencyCodeSchema.nullable().optional(),
  reportingCost: z.number().nullable().optional(),
  reportingCurrency: currencyCodeSchema.nullable().optional(),
  systemSku: z.boolean().optional(),
  orgId: z.string().nullable().optional(),
  archived: z.boolean().nullable().optional(),
}).passthrough()

/**
 * Sale schema per OpenAPI (SaleDTO).
 */
export const saleSchema = z.object({
  id: z.number(),
  uuid: z.string(), // backend sends a UUID string; avoid strict .uuid() in case of formatting differences
  orderKey: z.string().nullable().optional(),
  orderId: z.string(),
  omsOrderId: z.string().nullable().optional(),
  country: z.string(), // backend sends "PL", "CZ", etc.; accept string to avoid enum drift
  reportingCurrency: currencyCodeSchema,
  reportingUnitPrice: z.number(),
  quantity: z.number(),
  reportingTotal: z.number(),
  date: z.string(), // "YYYY-MM-DD"
  sku: saleSkuSchema,
  status: z.string(),
  statusDate: z.string(),
  invoiceIssueDate: z.string().nullable().optional(),
  invoiceSaleDate: z.string().nullable().optional(),
  sourceProductName: z.string().nullable().optional(),
  orderSource: z.string().nullable().optional(),
}).passthrough()

/**
 * Paginated response schema for sales.
 */
/**
 * Backend returns a Spring-style page payload. Keep only the fields we use but allow extras.
 */
export const salesResponseSchema = z
  .object({
    content: z.array(saleSchema),
    totalElements: z
      .union([z.number(), z.string(), z.object({}).passthrough()])
      .optional()
      .transform(val => {
        if (typeof val === 'number') return val
        if (typeof val === 'string') {
          const parsed = Number(val)
          return Number.isFinite(parsed) ? parsed : 0
        }
        return 0
      }),
    totalPages: z
      .union([z.number(), z.string(), z.object({}).passthrough()])
      .optional()
      .transform(val => {
        if (typeof val === 'number') return val
        if (typeof val === 'string') {
          const parsed = Number(val)
          return Number.isFinite(parsed) ? parsed : 0
        }
        return 0
      }),
    size: z
      .union([z.number(), z.string(), z.object({}).passthrough()])
      .optional()
      .transform(val => {
        if (typeof val === 'number') return val
        if (typeof val === 'string') {
          const parsed = Number(val)
          return Number.isFinite(parsed) ? parsed : 0
        }
        return 0
      }),
    number: z
      .union([z.number(), z.string(), z.object({}).passthrough()])
      .optional()
      .transform(val => {
        if (typeof val === 'number') return val
        if (typeof val === 'string') {
          const parsed = Number(val)
          return Number.isFinite(parsed) ? parsed : 0
        }
        return 0
      }),
  })
  .passthrough() // allow pageable, sort, etc.

export type SaleDTO = z.infer<typeof saleSchema>
export type SalesResponse = z.infer<typeof salesResponseSchema>
